import { useRef } from 'react';
import { toast } from 'react-toastify';
import Spinner from '../components/Spinner';
import { fileSizeLimit } from '../utils/consts';
import { documentVerify } from '../utils/firebase';
import { fileToBase64Image } from '../utils/image';
import { useModal } from './ModalProvider';

export default function VerifyAgeButton({ userId }) {
  const inputRef = useRef(null);
  const { openModal, closeModal } = useModal();

  const handleClick = () =>
    openModal({
      header: 'Age verification',
      body: (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          }}
        >
          <p className='font-16'>
            To verify your age, please upload an image of one of the following
            documents: <span className='font-medium'>Birth Certificate</span>,{' '}
            <span className='font-medium'>School Report Card</span>, or{' '}
            <span className='font-medium'>Passport</span>.
          </p>
          <p className='font-16'>
            <span className='font-medium'>Attention:</span> The document image
            must be clearly readable, and your profile name (case insensitive)
            and birth date must exactly match those in the document. Otherwise,
            you will not pass verification.
          </p>
          <p className='font-16'>
            <span className='font-medium'>Note:</span> We do not store your
            personal data - the document image is deleted immediately after
            verification.
          </p>
        </div>
      ),
      buttons: [
        { text: 'Upload document', onClick: () => inputRef.current?.click() },
      ],
    });

  const handleChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-select same file
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Wrong file type (image required)');
      return;
    }
    if (file.size > fileSizeLimit) {
      toast.error(
        `file size limit is ${(fileSizeLimit / (1024 * 1024)).toFixed(0)} MB`
      );
      return;
    }

    openModal({
      header: 'Verification...',
      body: (
        <>
          <p className='font-16'>Verification is in progress. Please wait.</p>
          <Spinner show={true} />
        </>
      ),
    });

    try {
      const base64 = await fileToBase64Image({ file });
      const res = await documentVerify({
        userId,
        file: base64,
        mime: 'image/jpeg',
      });

      closeModal();

      const { verified, name, dob, gender } = res.data;

      if (verified) {
        openModal({
          header: 'Verification Successful',
          body: (
            <p className='font-16'>Your age has been successfully verified.</p>
          ),
          buttons: [{ text: 'Ok' }],
        });
      } else {
        openModal({
          header: 'Verification Failed',
          body: (
            <>
              <p className='font-16'>
                We couldn’t complete verification because the following details
                from the document don’t match your profile:
              </p>
              <ul
                style={{
                  listStyle: 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  margin: '8px 0 8px 8px',
                }}
              >
                {!name && (
                  <li>
                    <span className='fa-cross-circle danger' /> Name
                  </li>
                )}
                {!dob && (
                  <li>
                    <span className='fa-cross-circle danger' /> Date of birth
                  </li>
                )}
                {!gender && (
                  <li>
                    <span className='fa-cross-circle danger' /> Gender
                  </li>
                )}
              </ul>
              <p className='font-16'>
                Please check the document and profile information, then try
                again.
              </p>
            </>
          ),
          buttons: [{ text: 'Ok' }],
        });
      }
    } catch (e) {
      toast.error(e?.message || 'Age verification failed');
      closeModal();
    }
  };

  return (
    <div className={`verify-age-btn`}>
      <button
        className={'check-doc small bw'}
        onClick={handleClick}
        type='button'
      >
        Verify age
      </button>
      <input
        ref={inputRef}
        type='file'
        accept='image/*'
        onChange={handleChosen}
        className='file-upload-input'
        style={{ display: 'none' }}
      />
    </div>
  );
}
